import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteForm, api } from '../../lib/api';
import type { FormListItem } from '../../types/form';
import { getFormPublicUrl } from '../../types/form';
import { useIsSuperadmin } from '../../stores/authStore';
import { useNotification } from '../ui/NotificationToast';
import CollaboratorSettings from '../builder/CollaboratorSettings';
import FormAnalytics from './FormAnalytics';
import LiveQuizAnalytics from '../live-quiz/analytics/LiveQuizAnalytics';
import { SkeletonFormList, SkeletonPageHeader } from '../ui/Skeleton';
import FormCreationWizard from './FormCreationWizard';
import ConfirmModal from '../common/ConfirmModal';

interface Group {
  id: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  memberCount: number;
}

interface NewInvitation {
  id: string;
  token: string;
  email: string;
  type: string;
  groupId?: string;
  groupName?: string;
  role: string;
  expiresAt: string;
}

export default function FormList() {
  useNavigate(); // Used by FormCreationWizard internally
  const { t } = useTranslation('admin');
  const isSuperadmin = useIsSuperadmin();
  const { showNotification } = useNotification();
  const [forms, setForms] = useState<FormListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInviteBanner, setShowInviteBanner] = useState(() => {
    return localStorage.getItem('hideInviteBanner') !== 'true';
  });

  // Share modal state
  const [shareModalFormId, setShareModalFormId] = useState<string | null>(null);
  const [analyticsFormId, setAnalyticsFormId] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Embed modal state
  const [embedModalForm, setEmbedModalForm] = useState<FormListItem | null>(null);
  const [embedTab, setEmbedTab] = useState<'iframe' | 'js'>('js');

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTab, setShareTab] = useState<'invite' | 'team'>('invite');
  const [groups, setGroups] = useState<Group[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [teamsEnabled, setTeamsEnabled] = useState(false);

  // Invitation form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    type: 'platform' as 'platform' | 'group',
    groupId: '',
    role: 'user' as 'admin' | 'user' | 'member',
    message: '',
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [newInvitation, setNewInvitation] = useState<NewInvitation | null>(null);
  const [copied, setCopied] = useState(false);

  // Confirm delete modal state
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    loadForms();
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Re-fetch when search changes
  useEffect(() => {
    loadForms(debouncedSearch);
  }, [debouncedSearch]);

  function handleOpenCreateModal() {
    setShowCreateModal(true);
  }

  async function loadForms(search?: string) {
    try {
      setLoading(true);
      const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await api.get(`/api/forms${searchParam}`);
      if (response.ok) {
        const data = await response.json() as { forms: FormListItem[] };
        setForms(data.forms);
      } else {
        throw new Error('Failed to load forms');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  }

  function handleDeleteForm(id: string, name: string) {
    setDeleteConfirm({ id, name });
  }

  async function confirmDelete() {
    if (!deleteConfirm) return;

    try {
      await deleteForm(deleteConfirm.id);
      setForms(forms.filter((f) => f.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete form');
      setDeleteConfirm(null);
    }
  }

  async function handleDuplicateForm(id: string, name: string) {
    try {
      const response = await api.post(`/api/forms/${id}/duplicate`, {
        name: `${name} (Copy)`,
      });

      if (!response.ok) {
        const data = await response.json() as { error?: string };
        throw new Error(data.error || 'Failed to duplicate form');
      }

      const data = await response.json() as { form: { id: string; name: string; slug: string } };
      showNotification('success', `Form duplicated: ${data.form.name}`);

      // Reload forms to show the new one
      loadForms();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to duplicate form');
    }
  }

  async function loadGroups() {
    setGroupsLoading(true);
    try {
      const response = await api.get('/api/groups');
      if (response.ok) {
        const data = await response.json() as { groups: Group[] };
        const invitableGroups = data.groups.filter(
          (g) => g.role === 'owner' || g.role === 'admin'
        );
        setGroups(invitableGroups);
        if (invitableGroups.length > 0 && !inviteForm.groupId) {
          setInviteForm((f) => ({ ...f, groupId: invitableGroups[0].id }));
        }
      }
    } catch {
      // Ignore errors
    } finally {
      setGroupsLoading(false);
    }
  }

  async function handleOpenShareModal() {
    setShowShareModal(true);
    setInviteError(null);
    setNewInvitation(null);
    setCopied(false);

    // Check if teams feature is enabled
    try {
      const response = await api.get('/api/settings/public');
      if (response.ok) {
        const data = await response.json() as { settings: { teams_enabled?: boolean } };
        setTeamsEnabled(data.settings?.teams_enabled ?? false);
      }
    } catch {
      // Default to disabled if can't fetch
      setTeamsEnabled(false);
    }

    if (shareTab === 'team' && groups.length === 0 && teamsEnabled) {
      loadGroups();
    }
  }

  async function handleSendInvitation(e: React.FormEvent) {
    e.preventDefault();
    setInviteLoading(true);
    setInviteError(null);

    try {
      const payload = inviteForm.type === 'platform'
        ? {
            email: inviteForm.email,
            type: 'platform' as const,
            role: inviteForm.role as 'admin' | 'user',
          }
        : {
            email: inviteForm.email,
            type: 'group' as const,
            groupId: inviteForm.groupId,
            role: inviteForm.role as 'admin' | 'member',
            message: inviteForm.message || undefined,
          };

      const response = await api.post('/api/invitations', payload);

      if (response.ok) {
        const data = await response.json() as { invitation: NewInvitation };
        setNewInvitation(data.invitation);
        setInviteForm((f) => ({ ...f, email: '', message: '' }));
      } else {
        const data = await response.json() as { error?: string };
        setInviteError(data.error || 'Failed to send invitation');
      }
    } catch {
      setInviteError('Failed to connect to server');
    } finally {
      setInviteLoading(false);
    }
  }

  function getInviteLink(token: string) {
    return `${window.location.origin}/invite/${token}`;
  }

  async function copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      showNotification('success', 'Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      showNotification('success', 'Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleShareViaEmail() {
    const subject = encodeURIComponent('Check out this Form Builder');
    const body = encodeURIComponent(
      `I've been using this amazing form builder and thought you might like it too!\n\nCheck it out: ${window.location.origin}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`);
  }

  function handleShareViaTwitter() {
    const text = encodeURIComponent('Check out this amazing form builder!');
    const url = encodeURIComponent(window.location.origin);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  }

  function handleShareViaLinkedIn() {
    const url = encodeURIComponent(window.location.origin);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  }

  if (loading) {
    return (
      <div>
        <SkeletonPageHeader />
        <SkeletonFormList count={6} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('formList.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">{t('formList.subtitle', 'Create and manage your forms')}</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('formList.search')}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-64"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          <button onClick={handleOpenCreateModal} className="btn-primary">
            <svg className="w-5 h-5 ltr:mr-2 rtl:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('createForm.create')}
          </button>
        </div>
      </div>

      {/* Invite Friends Banner - for regular users */}
      {!isSuperadmin && showInviteBanner && (
        <div className="mb-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white relative overflow-hidden">
          <button
            onClick={() => {
              setShowInviteBanner(false);
              localStorage.setItem('hideInviteBanner', 'true');
            }}
            className="absolute top-3 ltr:right-3 rtl:left-3 text-white/80 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-6">
            <div className="hidden sm:block">
              <svg className="w-16 h-16 text-white/90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-1">{t('inviteBanner.title')}</h3>
              <p className="text-white/90 text-sm mb-3">
                {t('inviteBanner.description')}
              </p>
              <button
                onClick={handleOpenShareModal}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg font-medium text-sm hover:bg-blue-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                {t('inviteBanner.button')}
              </button>
            </div>
          </div>
          {/* Decorative circles */}
          <div className="absolute ltr:-right-8 rtl:-left-8 -top-8 w-32 h-32 bg-white/10 rounded-full"></div>
          <div className="absolute ltr:-right-4 rtl:-left-4 -bottom-4 w-24 h-24 bg-white/5 rounded-full"></div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ltr:ml-4 rtl:mr-4 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
            {t('formList.dismiss')}
          </button>
        </div>
      )}

      {/* Forms Grid */}
      {forms.length === 0 ? (
        <div className="card dark:bg-gray-800 dark:border-gray-700 p-12 text-center">
          <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {debouncedSearch ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            )}
          </svg>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {debouncedSearch ? t('formList.noResults', 'No forms found') : t('formList.empty')}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            {debouncedSearch
              ? t('formList.noResultsDescription', { query: debouncedSearch })
              : t('formList.emptyDescription')}
          </p>
          {debouncedSearch ? (
            <button
              onClick={() => setSearchQuery('')}
              className="btn-secondary"
            >
              {t('formList.clearSearch', 'Clear Search')}
            </button>
          ) : (
            <button onClick={handleOpenCreateModal} className="btn-primary">
              {t('formList.createFirst')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {forms.map((form) => (
            <div key={form.id} className="card dark:bg-gray-800 dark:border-gray-700 p-4 sm:p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">{form.name}</h3>
                  {form.status === 'published' && (
                    <button
                      onClick={() => setEmbedModalForm(form)}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors flex-shrink-0"
                      title={t('tooltips.shareForm')}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  )}
                </div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ltr:ml-2 rtl:mr-2 ${
                    form.status === 'published'
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-400'
                      : form.status === 'archived'
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                      : 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-400'
                  }`}
                >
                  {t(`formList.status.${form.status}`, form.status)}
                </span>
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400 truncate mb-1">{form.description || t('formList.noDescription', 'No description')}</p>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                <p>{t('formList.version', 'Version')} {form.version} · {t('formList.updated', 'Updated')} {new Date(form.updated_at).toLocaleDateString()}</p>
              </div>

              <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                <Link
                  to={form.form_type === 'live_quiz' ? `/formspace/live-quiz/${form.id}` : `/formspace/builder/${form.id}`}
                  className={`p-2 sm:p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors ${
                    form.form_type === 'live_quiz'
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-gray-600 dark:text-gray-300'
                  }`}
                  title={form.form_type === 'live_quiz' ? t('tooltips.editLiveQuiz') : t('tooltips.edit')}
                >
                  {form.form_type === 'live_quiz' ? (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  )}
                </Link>
                {form.form_type !== 'live_quiz' && (
                  <Link
                    to={`/formspace/forms/${form.id}/submissions`}
                    className="p-2 sm:p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-colors"
                    title={t('tooltips.submissions')}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </Link>
                )}
                {form.status !== 'draft' && (
                  <button
                    onClick={() => setAnalyticsFormId(form.id)}
                    className="p-2 sm:p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors"
                    title={t('tooltips.analytics')}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </button>
                )}
                {form.status !== 'draft' && (
                  <button
                    onClick={() => handleDuplicateForm(form.id, form.name)}
                    className="p-2 sm:p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                    title={t('tooltips.duplicate')}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                )}
                {form.status !== 'draft' && (
                  <button
                    onClick={() => setShareModalFormId(form.id)}
                    className="p-2 sm:p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                    title={t('tooltips.collaborators')}
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => handleDeleteForm(form.id, form.name)}
                  className="p-2 sm:p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 dark:bg-gray-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                  title={t('tooltips.delete')}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Creation Wizard */}
      <FormCreationWizard
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {/* Share & Invite Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{t('shareInvite.title')}</h2>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setNewInvitation(null);
                  setInviteError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Success State - Show Invitation Link */}
            {newInvitation ? (
              <div className="p-6">
                <div className="text-center mb-4">
                  <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{t('shareInvite.invitationSent')}</h3>
                  <p className="text-gray-600 mt-1">
                    {t('shareInvite.shareWith')} <strong>{newInvitation.email}</strong>
                  </p>
                  {newInvitation.groupName && (
                    <p className="text-sm text-gray-500">
                      {t('shareInvite.toJoin')} <strong>{newInvitation.groupName}</strong>
                    </p>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('shareInvite.invitationLink')}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={getInviteLink(newInvitation.token)}
                      className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(getInviteLink(newInvitation.token))}
                      className={`px-4 py-2 rounded-lg transition-colors ${
                        copied
                          ? 'bg-green-600 text-white'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {copied ? t('shareInvite.copied') : t('shareInvite.copy')}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    {t('shareInvite.expires')} {new Date(newInvitation.expiresAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> {t('shareInvite.linkShownOnce')}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setNewInvitation(null)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    {t('shareInvite.sendAnother')}
                  </button>
                  <button
                    onClick={() => {
                      setShowShareModal(false);
                      setNewInvitation(null);
                    }}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {t('shareInvite.done')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Tab Navigation - only show tabs if teams feature is enabled */}
                {teamsEnabled && (
                  <div className="flex border-b border-gray-200">
                    <button
                      onClick={() => setShareTab('invite')}
                      className={`flex-1 px-4 py-3 text-sm font-medium ${
                        shareTab === 'invite'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                        {t('shareInvite.tabInvite')}
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShareTab('team');
                        if (groups.length === 0) loadGroups();
                      }}
                      className={`flex-1 px-4 py-3 text-sm font-medium ${
                        shareTab === 'team'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {t('shareInvite.tabTeam')}
                      </div>
                    </button>
                  </div>
                )}

                {/* Tab Content */}
                <div className="p-6">
                  {shareTab === 'invite' && (
                    <div>
                      {/* Quick Share Options */}
                      <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">{t('shareInvite.quickShare')}</h3>
                        <div className="grid grid-cols-3 gap-3">
                          <button
                            onClick={handleShareViaEmail}
                            className="flex flex-col items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs text-gray-600">{t('shareInvite.email')}</span>
                          </button>
                          <button
                            onClick={handleShareViaTwitter}
                            className="flex flex-col items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <svg className="w-6 h-6 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                            </svg>
                            <span className="text-xs text-gray-600">{t('shareInvite.twitter')}</span>
                          </button>
                          <button
                            onClick={handleShareViaLinkedIn}
                            className="flex flex-col items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <svg className="w-6 h-6 text-gray-600" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                            </svg>
                            <span className="text-xs text-gray-600">{t('shareInvite.linkedin')}</span>
                          </button>
                        </div>
                      </div>

                      {/* Copy Link */}
                      <div className="mb-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-3">{t('shareInvite.copyLink')}</h3>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            readOnly
                            value={window.location.origin}
                            className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm"
                          />
                          <button
                            onClick={() => copyToClipboard(window.location.origin)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            {t('shareInvite.copy')}
                          </button>
                        </div>
                      </div>

                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="bg-white px-3 text-gray-500">{t('shareInvite.orSendDirect')}</span>
                        </div>
                      </div>

                      {/* Direct Invitation Form - available to all users */}
                      <form onSubmit={handleSendInvitation}>
                        {inviteError && (
                          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                            {inviteError}
                          </div>
                        )}

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('shareInvite.emailAddress')}
                          </label>
                          <input
                            type="email"
                            required
                            value={inviteForm.email}
                            onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="friend@example.com"
                          />
                        </div>

                        {/* Only superadmins can assign roles */}
                        {isSuperadmin && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('shareInvite.role')}
                            </label>
                            <select
                              value={inviteForm.role}
                              onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value as 'admin' | 'user' }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="user">{t('shareInvite.roleUser')}</option>
                              <option value="admin">{t('shareInvite.roleAdmin')}</option>
                            </select>
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={inviteLoading}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        >
                          {inviteLoading ? t('shareInvite.sending') : t('shareInvite.sendInvitation')}
                        </button>

                        <p className="mt-3 text-xs text-gray-500 text-center">
                          {t('shareInvite.invitationNote')}
                        </p>
                      </form>
                    </div>
                  )}

                  {shareTab === 'team' && teamsEnabled && (
                    <div>
                      {groupsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                      ) : groups.length === 0 ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                          <svg className="w-8 h-8 text-yellow-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-yellow-800 font-medium">{t('shareInvite.noGroups')}</p>
                          <p className="text-xs text-yellow-700 mt-1">
                            {t('shareInvite.noGroupsDescription')}
                          </p>
                        </div>
                      ) : (
                        <form onSubmit={handleSendInvitation}>
                          {inviteError && (
                            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                              {inviteError}
                            </div>
                          )}

                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('shareInvite.selectGroup')}
                            </label>
                            <select
                              value={inviteForm.groupId}
                              onChange={(e) => setInviteForm((f) => ({ ...f, groupId: e.target.value, type: 'group' }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              required
                            >
                              {groups.map((group) => (
                                <option key={group.id} value={group.id}>
                                  {group.name} ({group.memberCount} {t('shareInvite.members')})
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('shareInvite.emailAddress')}
                            </label>
                            <input
                              type="email"
                              required
                              value={inviteForm.email}
                              onChange={(e) => setInviteForm((f) => ({ ...f, email: e.target.value, type: 'group' }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="user@example.com"
                            />
                          </div>

                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('shareInvite.roleInGroup')}
                            </label>
                            <select
                              value={inviteForm.role}
                              onChange={(e) => setInviteForm((f) => ({ ...f, role: e.target.value as 'admin' | 'member' }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="member">{t('shareInvite.roleMember')}</option>
                              <option value="admin">{t('shareInvite.roleAdmin')}</option>
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                              {t('shareInvite.adminCanManage')}
                            </p>
                          </div>

                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('shareInvite.personalMessage')} <span className="text-gray-400">({t('shareInvite.optional')})</span>
                            </label>
                            <textarea
                              value={inviteForm.message}
                              onChange={(e) => setInviteForm((f) => ({ ...f, message: e.target.value }))}
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder={t('shareInvite.addPersonalNote')}
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={inviteLoading || !inviteForm.groupId}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                          >
                            {inviteLoading ? t('shareInvite.sending') : t('shareInvite.sendTeamInvitation')}
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Collaborator Settings Modal */}
      {shareModalFormId && (
        <CollaboratorSettings
          formId={shareModalFormId}
          isOpen={true}
          onClose={() => setShareModalFormId(null)}
        />
      )}

      {/* Analytics Modal */}
      {analyticsFormId && (() => {
        const form = forms.find(f => f.id === analyticsFormId);
        const isLiveQuiz = form?.form_type === 'live_quiz';

        return isLiveQuiz ? (
          <LiveQuizAnalytics
            formId={analyticsFormId}
            sessionId=""
            isOpen={true}
            onClose={() => setAnalyticsFormId(null)}
          />
        ) : (
          <FormAnalytics
            formId={analyticsFormId}
            isOpen={true}
            onClose={() => setAnalyticsFormId(null)}
          />
        );
      })()}

      {/* Share Modal */}
      {embedModalForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('shareForm.title')}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{embedModalForm.name}</p>
              </div>
              <button
                onClick={() => {
                  setEmbedModalForm(null);
                  setEmbedTab('js');
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Form URL */}
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">{t('shareForm.formUrl')}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={window.location.origin + getFormPublicUrl(embedModalForm.slug, embedModalForm.form_type)}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono text-gray-700 dark:text-gray-300"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + getFormPublicUrl(embedModalForm.slug, embedModalForm.form_type));
                      showNotification('success', 'Form URL copied!');
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    {t('shareInvite.copy')}
                  </button>
                </div>
              </div>

              {/* Results Viewer URL */}
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                <label className="text-sm font-medium text-blue-700 dark:text-blue-400 block mb-1">{t('shareForm.resultsUrl')}</label>
                <p className="text-xs text-blue-600 dark:text-blue-400 mb-2">{t('shareForm.resultsDescription')}</p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={`${window.location.origin}/results/${embedModalForm.slug}`}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-600 rounded-lg text-sm font-mono text-gray-700 dark:text-gray-300"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/results/${embedModalForm.slug}`);
                      showNotification('success', 'Results URL copied!');
                    }}
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    {t('shareInvite.copy')}
                  </button>
                </div>
              </div>

              {/* Embed Code - Only show for non-live_quiz forms */}
              {embedModalForm.form_type !== 'live_quiz' && (
                <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-purple-700 dark:text-purple-400">{t('shareForm.embed')}</label>
                    <div className="flex rounded-lg overflow-hidden border border-purple-200 dark:border-purple-700">
                      <button
                        onClick={() => setEmbedTab('js')}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          embedTab === 'js'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50'
                        }`}
                      >
                        {t('shareForm.jsWidget')}
                      </button>
                      <button
                        onClick={() => setEmbedTab('iframe')}
                        className={`px-3 py-1 text-xs font-medium transition-colors ${
                          embedTab === 'iframe'
                            ? 'bg-purple-600 text-white'
                            : 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/50'
                        }`}
                      >
                        {t('shareForm.iframe')}
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-purple-600 dark:text-purple-400 mb-3">
                    {embedTab === 'js'
                      ? t('shareForm.jsDescription')
                      : t('shareForm.iframeDescription')}
                  </p>
                  <div className="relative">
                    <pre className="bg-gray-900 text-gray-100 rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all max-h-32">
                      {embedTab === 'js'
                        ? `<div data-myforms-embed="${window.location.origin}${getFormPublicUrl(embedModalForm.slug, embedModalForm.form_type)}" data-height="600"></div>
<script src="${window.location.origin}/embed.js"></script>`
                        : `<iframe src="${window.location.origin}${getFormPublicUrl(embedModalForm.slug, embedModalForm.form_type)}?embed=true" width="100%" height="600" frameborder="0"></iframe>`}
                    </pre>
                    <button
                      onClick={() => {
                        const formUrl = window.location.origin + getFormPublicUrl(embedModalForm.slug, embedModalForm.form_type);
                        const code = embedTab === 'js'
                          ? `<div data-myforms-embed="${formUrl}" data-height="600"></div>\n<script src="${window.location.origin}/embed.js"></script>`
                          : `<iframe src="${formUrl}?embed=true" width="100%" height="600" frameborder="0"></iframe>`;
                        navigator.clipboard.writeText(code);
                        showNotification('success', 'Embed code copied!');
                      }}
                      className="absolute top-2 ltr:right-2 rtl:left-2 px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
                    >
                      {t('shareInvite.copy')}
                    </button>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <button
                onClick={() => {
                  setEmbedModalForm(null);
                  setEmbedTab('js');
                }}
                className="w-full btn-secondary"
              >
                {t('shareForm.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={deleteConfirm !== null}
        title={t('confirm.deleteTitle', { ns: 'common' })}
        message={t('confirm.deleteMessage', { ns: 'common', name: deleteConfirm?.name || '' })}
        confirmText={t('confirm.delete', { ns: 'common' })}
        cancelText={t('confirm.cancel', { ns: 'common' })}
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
